import {Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR} from '@angular/forms';
import {DialogType, IoniconsOutline, WidgetValidator} from '@pt/pt-models';
import {Observable} from 'rxjs';
import {AcceptedFileConfig, fileValidator, FileValidatorResponse, ValidatorGenerator} from '../../utils';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {NgbModalRef} from "@ng-bootstrap/ng-bootstrap/modal/modal-ref";
import {DialogService} from "../../../services";
import {TranslateService} from "@ngx-translate/core";

@UntilDestroy()
@Component({
  selector: 'pt-input-image',
  templateUrl: './input-image.component.html',
  styleUrls: ['./input-image.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputImageComponent)
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputImageComponent),
      multi: true
    }
  ]
})
export class InputImageComponent implements OnInit, ControlValueAccessor, OnDestroy {
  @Input() fileConfig?: AcceptedFileConfig;
  @Input() controlName: string;
  @Input() validators: WidgetValidator[];
  @Input() label: string;
  @Input() fontWeightBold = false;
  @Input() placeholder: string;
  @Input() help: string;
  @Input() error: string;    // Da implementare
  @Input() reseller = false;
  @Input() deletable = false;
  @Input() info?: string;
  @Input() editable: boolean;
  @Input() defaultImage: string;
  @Input() confirmDelete: boolean;

  @Output() uploadImage = new EventEmitter<File>();
  @Output() deleteImage = new EventEmitter<boolean>();

  // eslint-disable-next-line
  @ViewChild('changeFile', {static: true}) changeFile: any;
  // eslint-disable-next-line
  @ViewChild('dialog', {static: true}) dialog: any;

  private currentDialog: NgbModalRef;

  private onTouched: () => void;
  // eslint-disable-next-line
  private onChanged: (val: any) => void;

  selectedFile: string | ArrayBuffer;
  originalFile: string | ArrayBuffer;
  Trash = IoniconsOutline.trash;
  isRequired: boolean;
  optional: Observable<string>;
  inputId: string;
  showImage = true;
  isChanged = false;
  deleteRequest = false;
  fileValidatorResponse?: FileValidatorResponse;

  constructor(
    private modalService: NgbModal,
    private dialogService: DialogService,
    private translateService: TranslateService,
  ) {
  }

  ngOnDestroy(): void {
    this.closeDialog();
  }

  closeDialog() {
    if (this.currentDialog) {
      this.currentDialog.close();
    }
  }

  delete() {
    if (this.confirmDelete) {
      const title = this.translateService.stream('TITLE.DELETE');
      const message = this.translateService.stream('MESSAGE.ARE_YOU_SURE');
      this.dialogService.runConfirmDialog({
        title, message, size: "sm",
        dialogType: DialogType.DELETE
      }).subscribe((confirmed) => {
        if (confirmed) {
          this.isChanged = true;
          this.deleteRequest = true;
          this.deleteImage.emit();
        }
      });
    } else {
      this.isChanged = true;
      this.deleteRequest = true;
      this.deleteImage.emit();
    }
  }

  // eslint-disable-next-line
  onFileChanged(event: any) {
    const file: File = event.target.files[0];
    this.deleteRequest = false;
    if (this.fileConfig) {
      fileValidator(file, this.fileConfig).pipe( //validate it
        untilDestroyed(this)
      ).subscribe({
        next: (response: FileValidatorResponse) => {
          this.changeFileValidatorResponse(response);
          if (response.isValid) {
            this.isChanged = true;
            this.uploadImage.emit(file);
            const reader = new FileReader();
            reader.onload = (e) => {
              this.updateValue(e.target.result);
            };
            reader.readAsDataURL(file);
          }
        },
        error: (e) => {
          console.error('Error ', e);
        }
      });
    } else {
      this.uploadImage.emit(file);
      const reader = new FileReader();
      reader.onload = (e) => this.updateValue(e.target.result);
      reader.readAsDataURL(file);
    }
  }

  updateValue(value: string | ArrayBuffer) {
    this.selectedFile = value ? value : this.defaultImage;
    if (this.onChanged) {
      this.onChanged(this.selectedFile);
    }
    if (this.onTouched) {
      this.onTouched();
    }
  }

  ngOnInit(): void {
    this.inputId = 'ptWInputImage_' + this.controlName;

    this.isRequired = ValidatorGenerator.validatorsContainsRequired(this.validators);

    this.selectedFile = this.defaultImage;
  }

  // eslint-disable-next-line
  writeValue(obj: any): void {
    this.selectedFile = obj ? obj : this.defaultImage;
    this.originalFile = this.selectedFile;
    if (!this.deleteRequest) {
      this.isChanged = false;
    }
  }

  // eslint-disable-next-line
  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  // eslint-disable-next-line
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate() {
    return this.selectedFile === null && {
      invalid: true
    };
  }

  private changeFileValidatorResponse(currentValue: FileValidatorResponse) {
    this.fileValidatorResponse = currentValue;
    if (!currentValue.isValid) {
      this.showImage = false;
      this.selectedFile = this.originalFile;
      setTimeout(() => {
        this.showImage = true;
        switch (currentValue.error) {
          case 'fileTypeError':
          case 'heightFileError':
          case 'tooLargeFileError':
          case 'widthFileError':
          case 'widthHeightFileError':
            this.openErrorDialog();
            break;
        }
      }, 500)

    }
  }

  openErrorDialog() {
    if (this.dialog) {
      this.currentDialog = this.modalService.open(this.dialog, {
        centered: true,
        keyboard: false,
        backdrop: 'static',
        size: 'md'
      });
    }
  }

}
